/**
 * ItemGo Service Move — Pricing Engine
 *
 * Each vehicle has its own base fare and per-mile rate.
 * Pricing = baseFare + distanceCharge + addOns, multiplied by difficulty.
 * No per-minute charge. System recommends vehicle based on volume estimate.
 * All values in pence (GBP). VAT on (servicePrice + serviceFee).
 * Everything configurable from Firestore pluginConfigs/{serviceId}.
 */
export const DEFAULT_MOVE_PRICING_CONFIG = {
    serviceFee: 599, // £5.99
    vatRate: 0.20,
    nonSubscriptionCommissionRate: 0.20,
    stripeFeeRate: 0.014, // 1.4% — UK Stripe card rate
    stripeFeeFixed: 20, // 20p
    vehicleBaseFare: {
        car: 850,
        van_s: 1800,
        van_m: 2400,
        van_l: 3000,
        van_xl: 4080,
        luton: 5500,
        hgv_7_5t: 7500,
        towtruck: 8000,
    },
    vehiclePerMile: {
        car: 80,
        van_s: 85,
        van_m: 90,
        van_l: 95,
        van_xl: 105,
        luton: 120,
        hgv_7_5t: 235,
        towtruck: 250,
    },
    difficultyMultipliers: {
        easy: 1.0,
        normal: 1.15,
        difficult: 1.3,
        very_difficult: 1.5,
    },
    assemblyRate: 2000, // £20.00 per item
    packingRate: 3500, // £35.00 per room
    unpackingRate: 2500, // £25.00 per room
    pianoSurcharge: 8000, // £80.00
    specialItemRate: 1500, // £15.00 per item
};
/**
 * Calculate move pricing.
 * Pricing = (baseFare + distanceCharge + addOns) × difficultyMultiplier
 */
export function calculateMovePricing(input) {
    const config = input.config;
    const vehicleType = input.vehicleType || 'van_s';
    const baseFare = config.vehicleBaseFare[vehicleType] ?? config.vehicleBaseFare.van_s;
    const perMile = config.vehiclePerMile[vehicleType] ?? config.vehiclePerMile.van_s;
    const distanceCharge = Math.round(input.distanceMiles * perMile);
    // Add-ons
    const addOnDetails = [];
    let addOnsTotal = 0;
    const assemblyCharge = input.assemblyItems > 0
        ? input.assemblyItems * config.assemblyRate
        : 0;
    if (assemblyCharge > 0) {
        addOnDetails.push({ label: "Furniture assembly/disassembly", quantity: input.assemblyItems, unitPrice: config.assemblyRate, total: assemblyCharge });
        addOnsTotal += assemblyCharge;
    }
    const packingCharge = input.packingRooms > 0
        ? input.packingRooms * config.packingRate
        : 0;
    if (packingCharge > 0) {
        addOnDetails.push({ label: "Packing service", quantity: input.packingRooms, unitPrice: config.packingRate, total: packingCharge });
        addOnsTotal += packingCharge;
    }
    const unpackingCharge = input.unpackingRooms > 0
        ? input.unpackingRooms * config.unpackingRate
        : 0;
    if (unpackingCharge > 0) {
        addOnDetails.push({ label: "Unpacking service", quantity: input.unpackingRooms, unitPrice: config.unpackingRate, total: unpackingCharge });
        addOnsTotal += unpackingCharge;
    }
    const pianoSurcharge = input.hasPiano ? config.pianoSurcharge : 0;
    if (pianoSurcharge > 0) {
        addOnDetails.push({ label: "Piano", quantity: 1, unitPrice: config.pianoSurcharge, total: pianoSurcharge });
        addOnsTotal += pianoSurcharge;
    }
    const specialItemsCharge = input.specialItemsCount > 0
        ? input.specialItemsCount * config.specialItemRate
        : 0;
    if (specialItemsCharge > 0) {
        addOnDetails.push({ label: "Special items", quantity: input.specialItemsCount, unitPrice: config.specialItemRate, total: specialItemsCharge });
        addOnsTotal += specialItemsCharge;
    }
    // Subtotal before difficulty multiplier
    const subtotal = baseFare + distanceCharge + addOnsTotal;
    // Apply difficulty multiplier
    const difficultyMultiplier = config.difficultyMultipliers[input.difficulty] ?? 1.0;
    const servicePrice = Math.round(subtotal * difficultyMultiplier);
    // Platform fee and VAT
    const serviceFee = config.serviceFee ?? 599;
    const vatRate = config.vatRate ?? 0.20;
    const subtotalBeforeVat = servicePrice + serviceFee;
    const vatAmount = Math.round(subtotalBeforeVat * vatRate);
    const customerTotal = subtotalBeforeVat + vatAmount;
    // Commission and earnings
    const commissionRate = config.nonSubscriptionCommissionRate ?? 0.20;
    const commission = Math.round(servicePrice * commissionRate);
    const driverEarnings = servicePrice - commission;
    const platformRevenue = serviceFee + commission + vatAmount;
    const stripeFee = Math.round(customerTotal * config.stripeFeeRate) + config.stripeFeeFixed;
    return {
        vehicleType,
        baseFare,
        distanceCharge,
        difficultyMultiplier,
        assemblyCharge,
        packingCharge,
        unpackingCharge,
        pianoSurcharge,
        specialItemsCharge,
        addOnsTotal,
        addOnDetails,
        servicePrice,
        serviceFee,
        subtotalBeforeVat,
        vatAmount,
        customerTotal,
        commissionRate,
        commission,
        platformRevenue,
        driverEarnings,
        stripeFee,
        formatted: {
            baseFare: formatPence(baseFare),
            distanceCharge: formatPence(distanceCharge),
            assemblyCharge: formatPence(assemblyCharge),
            packingCharge: formatPence(packingCharge),
            unpackingCharge: formatPence(unpackingCharge),
            pianoSurcharge: formatPence(pianoSurcharge),
            specialItemsCharge: formatPence(specialItemsCharge),
            servicePrice: formatPence(servicePrice),
            serviceFee: formatPence(serviceFee),
            vatAmount: formatPence(vatAmount),
            customerTotal: formatPence(customerTotal),
        },
    };
}
/**
 * Full pricing engine used by the React hook and legacy web flows.
 * Takes pre-computed component charges and layers on fees, VAT, commission,
 * discounts and Stripe fees.
 */
export function calculateFullPricing(input) {
    const config = input.pricingConfig ?? DEFAULT_MOVE_PRICING_CONFIG;
    const baseFare = input.baseFare;
    const distanceCharge = input.distanceCharge;
    const timeCharge = input.timeCharge;
    const vehicleFee = input.vehicleFee ?? 0;
    const addOnsTotal = input.addOnsTotal ?? 0;
    const addOnDetails = input.addOnDetails;
    const servicePrice = input.serviceTotal ?? baseFare + distanceCharge + timeCharge + vehicleFee + addOnsTotal;
    const serviceFee = input.pricingConfig?.serviceFee ?? config.serviceFee;
    const commissionRate = input.commissionRate ?? input.pricingConfig?.nonSubscriptionCommissionRate ?? config.nonSubscriptionCommissionRate;
    const commission = Math.round(servicePrice * commissionRate);
    const subtotalBeforeVat = servicePrice + serviceFee;
    let discount = 0;
    if (input.promoDiscount) {
        if (input.promoDiscount.type === "percentage") {
            discount = Math.round(subtotalBeforeVat * (input.promoDiscount.value / 100));
        }
        else {
            discount = Math.min(input.promoDiscount.value, subtotalBeforeVat);
        }
    }
    const subtotalAfterDiscount = subtotalBeforeVat - discount;
    const vatAmount = Math.round(subtotalAfterDiscount * config.vatRate);
    const customerTotal = subtotalAfterDiscount + vatAmount;
    const driverEarnings = servicePrice - commission;
    const platformRevenue = serviceFee + commission + vatAmount;
    const stripeFeeRate = config.stripeFeeRate ?? 0.025;
    const stripeFeeFixed = config.stripeFeeFixed ?? 20;
    const stripeFee = Math.round(customerTotal * stripeFeeRate) + stripeFeeFixed;
    return {
        baseFare,
        distanceCharge,
        timeCharge,
        addOnsTotal,
        addOnDetails,
        servicePrice,
        serviceFee,
        subtotalBeforeVat,
        discount,
        subtotalAfterDiscount,
        vatAmount,
        customerTotal,
        commissionRate,
        commission,
        platformRevenue,
        driverEarnings,
        stripeFee,
    };
}
export function formatPence(pence) {
    return `£${(pence / 100).toFixed(2)}`;
}
export function formatCommissionRate(rate) {
    return `${(rate * 100).toFixed(0)}%`;
}
//# sourceMappingURL=pricing.js.map